<template>
    <div id="reservation-form" class="mx-auto w-full max-w-6xl space-y-6 rounded-2xl bg-primary-light p-6 shadow-lg">
        <!-- Reservation header -->
        <header class="overflow-hidden rounded-3xl border border-primary-medium bg-dark text-primary-lighter shadow-xl ring-1 ring-primary-medium/20">
            <div class="bg-gradient-to-r from-dark via-primary-dark to-primary-dark px-4 py-3 sm:px-5 sm:py-4">
                <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div class="space-y-1.5">
                        <span
                            class="inline-flex rounded-full border border-primary-lighter/15 bg-white/10 px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.22em] text-primary-light uppercase"
                        >
                            {{ t('reservation.header.kicker') }}
                        </span>
                        <div class="space-y-0.5">
                            <h1 class="text-lg font-bold tracking-tight sm:text-xl">
                                {{ t('reservation.header.title') }}
                            </h1>
                            <p class="max-w-2xl text-xs leading-5 text-primary-light/80 sm:text-sm sm:leading-6">
                                {{ t('reservation.header.subtitle') }}
                            </p>
                        </div>
                    </div>

                    <div
                        class="w-full max-w-[12.5rem] rounded-2xl border border-primary-lighter/15 bg-white/10 p-2.5 shadow-lg backdrop-blur-sm sm:p-3"
                    >
                        <div class="flex items-center justify-between gap-2.5">
                            <div>
                                <p class="text-[10px] font-semibold tracking-[0.22em] text-primary-light/80 uppercase">
                                    {{ t('reservation.header.stepLabel') }}
                                </p>
                                <p class="mt-0.5 text-sm font-bold text-primary-lighter sm:text-base">{{ step }} {{ t('reservation.of') }} 6</p>
                            </div>
                            <span class="rounded-full bg-primary-lighter px-2 py-0.5 text-[10px] font-bold tracking-[0.18em] text-dark uppercase">
                                {{ stepProgressPercentage }}%
                            </span>
                        </div>

                        <div class="mt-2.5 h-1.5 overflow-hidden rounded-full bg-white/10">
                            <div
                                class="h-full rounded-full bg-gradient-to-r from-primary-light to-primary transition-all duration-300"
                                :style="{ width: `${stepProgressPercentage}%` }"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!-- STEP 1: Trip details -->
        <TripDetailsForm
            ref="tripDetailsFormRef"
            v-if="step === 1"
            v-model:tripType="tripType"
            v-model:onewayDirection="onewayDirection"
            v-model:from="from"
            v-model:dropTo="dropTo"
            v-model:hotel="hotel"
            v-model:adults="adults"
            v-model:children="children"
            v-model:babies="babies"
            v-model:dateOneWay="dateOneWay"
            v-model:dateArrival="dateArrival"
            v-model:dateDeparture="dateDeparture"
            v-model:timeOneWay="timeOneWay"
            v-model:timeArrival="timeArrival"
            v-model:timeDeparture="timeDeparture"
            :pickUpTimeOneWay="pickUpTimeOneWay"
            :pickUpTimeDeparture="pickUpTimeDeparture"
            :dateFormatHint="dateFormatHint"
            :locationsWithKeys="locationsWithKeys"
            @previous="emit('backToQuickPrice')"
            @next="goToStep(2)"
        />

        <!-- STEP 2: Travel reference -->
        <TravelReferenceForm
            v-if="step === 2"
            v-model:travelType="travelType"
            v-model:travelNumber="travelNumber"
            v-model:vehicleType="vehicleType"
            v-model:babySeat="babySeat"
            v-model:childSeat="childSeat"
            v-model:numberOfStrollers="numberOfStrollers"
            v-model:numberOfSuitcases="numberOfSuitcases"
            :totalPassengers="totalPassengers"
            :vehicleTypes="vehicleTypes"
            :attemptedNext="attemptedNextStep2"
            @previous="goToStep(1)"
            @next="goToStep(3)"
        />

        <!-- STEP 3: Client info -->
        <ClientInfoForm v-if="step === 3" v-model:client="client" :attemptedNext="attemptedNextStep3" @previous="goToStep(2)" @next="goToStep(4)" />

        <!-- STEP 4: Rules & info -->
        <BookingRules v-if="step === 4" :from="from" :dropTo="dropTo" />

        <!-- STEP 5: Summary & submit -->
        <BookingSummary
            v-if="step === 5"
            :tripType="tripType"
            :onewayDirection="onewayDirection"
            :from="from"
            :dropTo="dropTo"
            :hotel="hotel"
            :adults="adults"
            :children="children"
            :babies="babies"
            :babySeat="babySeat"
            :childSeat="childSeat"
            :numberOfStrollers="numberOfStrollers"
            :numberOfSuitcases="numberOfSuitcases"
            :totalPassengers="totalPassengers"
            :dateOneWay="dateOneWay"
            :dateArrival="dateArrival"
            :dateDeparture="dateDeparture"
            :timeOneWayStr="timeOneWayStr"
            :timeArrivalStr="timeArrivalStr"
            :timeDepartureStr="timeDepartureStr"
            :pickUpTimeOneWay="pickUpTimeOneWay"
            :pickUpTimeDeparture="pickUpTimeDeparture"
            :travelType="travelType"
            :travelNumber="travelNumber"
            :vehicleType="vehicleType"
            :client="client"
            :privacyAccepted="privacyAccepted"
            @update:privacyAccepted="privacyAccepted = $event"
            @previous="goToStep(4)"
            @submit="submitBooking"
        />

        <!-- STEP 6: Thank you -->
        <ThankYouMessage v-if="step === 6" :reservationReference="reservationReference" />

        <!-- Price Display (common for all steps) -->
        <div
            :class="[
                { 'price-bump': priceAnimating },
                priceChangeDirection === 'up' ? 'price-up' : '',
                priceChangeDirection === 'down' ? 'price-down' : '',
            ]"
        >
            <PriceDisplay :priceDisplay="priceDisplay" :showNightInfo="false" card="reservation" />
        </div>

        <!-- WhatsApp Contact and Pricing Grid Links (common for all steps except step 6) -->
        <div v-if="step !== 6" class="mt-4 flex flex-col items-center gap-2">
            <button @click="showPricingModal = true" class="text-center text-sm font-medium text-primary transition hover:underline">
                📋 {{ t('reservation.viewPricing') }}
            </button>
        </div>

        <!-- Pricing Modal -->
        <Modal :show="showPricingModal" @close="showPricingModal = false">
            <PricingTable />
        </Modal>

        <!-- Navigation (common for all steps except 1) -->
        <div v-if="step !== 1 && step !== 5 && step !== 6" class="mt-4 flex justify-between">
            <button v-if="step > 1" type="button" @click="goToStep(step - 1)" class="btn btn-secondary">
                {{ t('reservation.navigation.previous') }}
            </button>
            <button v-if="step < 6" type="button" @click="handleNext" :class="nextButtonClass">
                {{ t('reservation.navigation.next') }}
            </button>
        </div>

        <!-- Navigation for step 5 (Summary) -->
        <div v-if="step === 5" class="mt-4 flex justify-between">
            <button type="button" @click="goToStep(4)" class="btn btn-secondary">
                {{ t('reservation.navigation.previous') }}
            </button>
        </div>

        <!-- Navigation for step 6 (Thank You) -->
        <div v-if="step === 6" class="mt-4 flex justify-between">
            <button type="button" @click="startNewBooking" class="btn btn-primary">
                {{ t('reservation.step6.newBooking') }}
            </button>
        </div>

        <!-- Navigation for step 1 (Trip Details) -->
        <div v-if="step === 1" class="mt-4 flex justify-between">
            <button type="button" @click="emit('backToQuickPrice')" class="btn btn-secondary">
                {{ t('reservation.navigation.previous') }}
            </button>
            <button
                type="button"
                @click="handleStep1Next"
                :disabled="!canProceedStep1"
                :class="['btn btn-shadow', canProceedStep1 ? 'btn-primary' : 'btn-muted']"
            >
                {{ t('reservation.navigation.next') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import Modal from '@/Components/Molecules/Modal.vue';
import PriceDisplay from '@/Components/Molecules/PriceDisplay.vue';
import PricingTable from '@/Components/Molecules/PricingTable.vue';
import { usePriceCalculation } from '@/composables/usePriceCalculation';
import { useTranslations } from '@/composables/useTranslations';
import { usePage } from '@inertiajs/vue3';
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import BookingRules from './Reservation/BookingRules.vue';
import BookingSummary from './Reservation/BookingSummary.vue';
import ClientInfoForm from './Reservation/ClientInfoForm.vue';
import ThankYouMessage from './Reservation/ThankYouMessage.vue';
import TravelReferenceForm from './Reservation/TravelReferenceForm.vue';
import TripDetailsForm from './Reservation/TripDetailsForm.vue';

const tripDetailsFormRef = ref<InstanceType<typeof TripDetailsForm> | null>(null);

type PrefillPayload = {
    tripType?: 'oneway' | 'roundtrip';
    from?: string;
    dropTo?: string;
    passengers?: number | string;
    [key: string]: unknown;
};

const props = defineProps<{ prefill?: PrefillPayload }>();
const emit = defineEmits<{
    (e: 'backToQuickPrice'): void;
}>();
const { t, locale } = useTranslations();
const page = usePage();

const STORAGE_KEY = 'reservation.form';
const SHARED_TRIP_KEY = 'trip.shared';
const isBrowser = typeof window !== 'undefined';
const STATE_EXPIRY_HOURS = 0.1;
const hydratedFromStorage = ref(false);
const showPricingModal = ref(false);
const isSubmitting = ref(false);
const bookingCompleted = ref(false);
const reservationReference = ref('');
const priceAnimating = ref(false);
const priceChangeDirection = ref<'up' | 'down' | null>(null);
let priceAnimTimer: ReturnType<typeof setTimeout> | null = null;

// Steps
const step = ref(1);
function goToStep(n: number) {
    if (bookingCompleted.value && n !== 6 && n !== 1) {
        return;
    }
    step.value = n;
}

const stepProgressPercentage = computed(() => Math.round((step.value / 6) * 100));

function blockHistoryBack() {
    if (!isBrowser || !bookingCompleted.value) return;
    window.history.pushState(null, '', window.location.href);
}

function focusCurrentStepElement() {
    if (!isBrowser) return;

    const container = document.getElementById('reservation-form');
    if (!container) return;

    const selector = [
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const focusTarget = container.querySelector<HTMLElement>(selector);
    focusTarget?.focus();
}

// Prefill from QuickPrice via Inertia props
const prefillFromPage = computed<PrefillPayload>(() => (page.props as any).prefill || {});
const prefillData = computed<PrefillPayload>(() => props.prefill ?? prefillFromPage.value);
const reservationSettings = computed(() => (page.props as any).reservationSettings || {});
const vehicleTypes = computed<string[]>(() => reservationSettings.value.vehicleTypes || []);

// Trip type and direction
const tripType = ref<'oneway' | 'roundtrip'>(prefillData.value.tripType || 'oneway');
const onewayDirection = ref<'arrival' | 'departure'>('arrival');

// Locations
const from = ref<string>(prefillData.value.from || '');
const dropTo = ref<string>(prefillData.value.dropTo || '');
const hotel = ref<string>('');

const locationsWithKeys = computed(() => {
    const translations = (page.props as any).translations || {};
    const locale = (page.props as any).locale || 'en';
    const currentTranslations = translations[locale] || translations.en || {};
    const locationsObj = currentTranslations.locations || {};

    // Return array of [key, value] pairs
    return Object.entries(locationsObj);
});

// Passengers split
const adults = ref<number>(Number(prefillData.value.passengers || 1));
const children = ref<number>(0);
const babies = ref<number>(0);
const totalPassengers = computed(() => adults.value + children.value + babies.value);

// Equipment
const babySeat = ref<number>(babies.value);
const childSeat = ref<number>(children.value);
const numberOfStrollers = ref<number>(children.value + babies.value);
const numberOfSuitcases = ref<number>(totalPassengers.value);
const equipmentInitialized = ref(false);

// Date format hint based on locale
const dateFormatHint = computed(() => {
    const l = locale.value;
    if (l === 'fr') return 'jj/mm/aaaa';
    if (l === 'es') return 'dd/mm/aaaa';
    return 'mm/dd/yyyy';
});

// Time helpers - convert HH:mm to minutes and back
function timeToMinutes(time: string): number {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
}

function minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function calculatePickUpTime(time: string): string {
    if (!time) return '';
    const minutes = timeToMinutes(time);
    const pickUpMinutes = (minutes - 180 + 1440) % 1440; // -3h with wrap
    return minutesToTime(pickUpMinutes);
}

// Times as HH:mm strings
const timeOneWay = ref<string>('');
const timeArrival = ref<string>('');
const timeDeparture = ref<string>('');

// Computed strings for display
const timeOneWayStr = computed(() => timeOneWay.value);
const timeArrivalStr = computed(() => timeArrival.value);
const timeDepartureStr = computed(() => timeDeparture.value);
const pickUpTimeOneWay = computed(() => calculatePickUpTime(timeOneWay.value));
const pickUpTimeDeparture = computed(() => calculatePickUpTime(timeDeparture.value));

// Dates
const dateOneWay = ref<string>('');
const dateArrival = ref<string>('');
const dateDeparture = ref<string>('');

// Price calculation composable
const passengersRef = computed(() => totalPassengers.value);
const { basePrice, nightPrice, finalPrice, hasNightTrip, priceDisplay } = usePriceCalculation({
    tripType,
    from,
    dropTo,
    passengers: passengersRef,
    timeOneWay,
    timeArrival,
    timeDeparture,
});

// Travel info
const travelType = ref<'flight' | 'train' | ''>('');
const travelNumber = ref<string>('');
const vehicleType = ref<string>('');

// Client info
const client = ref({ name: '', email: '', phone: '', notes: '' });
const privacyAccepted = ref(false);
const attemptedNextStep2 = ref(false);

// Validation for step 1
const canProceedStep1 = computed(() => {
    const locationsOk = from.value && dropTo.value;
    const paxOk = totalPassengers.value > 0 && totalPassengers.value <= 16;
    const timeOk =
        tripType.value === 'oneway'
            ? !!dateOneWay.value && !!timeOneWay.value
            : !!dateArrival.value && !!timeArrival.value && !!dateDeparture.value && !!timeDeparture.value;
    return locationsOk && paxOk && timeOk;
});

const canProceedStep2 = computed(() => vehicleType.value.trim().length > 0);

const nextButtonClass = computed(() => {
    return 'btn btn-primary btn-shadow';
});

function applyPrefill(p: PrefillPayload) {
    if (!p) return;
    if (p.tripType) tripType.value = p.tripType;
    if (p.from) from.value = p.from;
    if (p.dropTo) dropTo.value = p.dropTo;
    if (p.passengers !== undefined) {
        adults.value = Number(p.passengers) || 1;
        children.value = 0;
        babies.value = 0;
    }
}

function parseFresh<T>(raw: string | null): T | null {
    if (!raw) return null;
    try {
        const data = JSON.parse(raw);
        if (!data?.timestamp) return data;
        const now = Date.now();
        const expiryTime = STATE_EXPIRY_HOURS * 60 * 60 * 1000;
        if (now - data.timestamp > expiryTime) return null;
        return data;
    } catch {
        return null;
    }
}

function hydrateFromStorage() {
    if (!isBrowser) return;
    try {
        const stored = parseFresh<any>(window.localStorage.getItem(STORAGE_KEY));
        const shared = parseFresh<any>(window.localStorage.getItem(SHARED_TRIP_KEY));
        const data = stored || shared;
        if (!data) return;

        if (data.step) step.value = data.step;
        if (data.bookingCompleted !== undefined) bookingCompleted.value = !!data.bookingCompleted;
        if (typeof data.reservationReference === 'string') reservationReference.value = data.reservationReference;
        if (data.tripType) tripType.value = data.tripType;
        if (data.onewayDirection) onewayDirection.value = data.onewayDirection;
        if (typeof data.from === 'string') from.value = data.from;
        if (typeof data.dropTo === 'string') dropTo.value = data.dropTo;
        if (data.adults !== undefined) adults.value = Number(data.adults) || 1;
        if (data.children !== undefined) children.value = Number(data.children) || 0;
        if (data.babies !== undefined) babies.value = Number(data.babies) || 0;
        if (shared?.passengers !== undefined && stored === null) {
            // Align with quick price when coming from there
            adults.value = Number(shared.passengers) || 1;
            children.value = 0;
            babies.value = 0;
        }
        if (data.babySeat !== undefined) babySeat.value = Number(data.babySeat) || 0;
        else babySeat.value = 0;
        if (data.childSeat !== undefined) childSeat.value = Number(data.childSeat) || 0;
        else childSeat.value = 0;
        if (data.numberOfStrollers !== undefined) numberOfStrollers.value = Number(data.numberOfStrollers) || 0;
        else numberOfStrollers.value = 0;
        if (data.numberOfSuitcases !== undefined) numberOfSuitcases.value = Number(data.numberOfSuitcases) || 0;
        else numberOfSuitcases.value = 0;
        equipmentInitialized.value = true;
        if (typeof data.hotel === 'string') hotel.value = data.hotel;
        if (typeof data.dateOneWay === 'string') dateOneWay.value = data.dateOneWay;
        if (typeof data.dateArrival === 'string') dateArrival.value = data.dateArrival;
        if (typeof data.dateDeparture === 'string') dateDeparture.value = data.dateDeparture;
        if (typeof data.timeOneWay === 'string') timeOneWay.value = data.timeOneWay;
        if (typeof data.timeArrival === 'string') timeArrival.value = data.timeArrival;
        if (typeof data.timeDeparture === 'string') timeDeparture.value = data.timeDeparture;
        if (data.travelType) travelType.value = data.travelType;
        if (typeof data.travelNumber === 'string') travelNumber.value = data.travelNumber;
        if (typeof data.vehicleType === 'string') vehicleType.value = data.vehicleType;
        if (data.client) {
            client.value = {
                name: data.client.name || '',
                email: data.client.email || '',
                phone: data.client.phone || '',
                notes: data.client.notes || '',
            };
        }
        privacyAccepted.value = !!data.privacyAccepted;
        hydratedFromStorage.value = true;
    } catch (e) {
        console.error('Failed to load reservation state', e);
    }
}

function persistState() {
    if (!isBrowser) return;
    const payload = {
        step: step.value,
        tripType: tripType.value,
        onewayDirection: onewayDirection.value,
        from: from.value,
        dropTo: dropTo.value,
        adults: adults.value,
        children: children.value,
        babies: babies.value,
        babySeat: babySeat.value,
        childSeat: childSeat.value,
        numberOfStrollers: numberOfStrollers.value,
        numberOfSuitcases: numberOfSuitcases.value,
        hotel: hotel.value,
        dateOneWay: dateOneWay.value,
        dateArrival: dateArrival.value,
        dateDeparture: dateDeparture.value,
        timeOneWay: timeOneWay.value,
        timeArrival: timeArrival.value,
        timeDeparture: timeDeparture.value,
        travelType: travelType.value,
        travelNumber: travelNumber.value,
        vehicleType: vehicleType.value,
        client: { ...client.value },
        privacyAccepted: privacyAccepted.value,
        bookingCompleted: bookingCompleted.value,
        reservationReference: reservationReference.value,
        timestamp: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Sync with quick price shared state
    const shared = {
        tripType: tripType.value,
        from: from.value,
        dropTo: dropTo.value,
        passengers: totalPassengers.value,
        timestamp: Date.now(),
    };
    window.localStorage.setItem(SHARED_TRIP_KEY, JSON.stringify(shared));
}

watch(prefillData, (p) => {
    if (hydratedFromStorage.value) return;
    applyPrefill(p);
});

watch(
    [
        step,
        tripType,
        onewayDirection,
        from,
        dropTo,
        hotel,
        adults,
        children,
        babies,
        dateOneWay,
        dateArrival,
        dateDeparture,
        timeOneWay,
        timeArrival,
        timeDeparture,
        travelType,
        travelNumber,
        vehicleType,
        client,
        privacyAccepted,
        bookingCompleted,
        reservationReference,
    ],
    persistState,
    { deep: true },
);

// Watch totalPassengers to initialize and clamp equipment values
watch(totalPassengers, (newTotal) => {
    if (!equipmentInitialized.value) {
        babySeat.value = 0;
        childSeat.value = 0;
        numberOfStrollers.value = 0;
        numberOfSuitcases.value = 0;
        equipmentInitialized.value = true;
        return;
    }
    if (babySeat.value > newTotal) babySeat.value = newTotal;
    if (childSeat.value > newTotal) childSeat.value = newTotal;
    if (numberOfStrollers.value > newTotal) numberOfStrollers.value = newTotal;
    if (numberOfSuitcases.value > 16) numberOfSuitcases.value = 16;
});

watch(finalPrice, (newPrice, oldPrice) => {
    if (oldPrice === undefined || newPrice === oldPrice) return;

    priceChangeDirection.value = newPrice > oldPrice ? 'up' : 'down';
    priceAnimating.value = false;
    requestAnimationFrame(() => {
        priceAnimating.value = true;
    });

    if (priceAnimTimer) clearTimeout(priceAnimTimer);
    priceAnimTimer = setTimeout(() => {
        priceAnimating.value = false;
        priceChangeDirection.value = null;
    }, 700);
});

watch(step, async () => {
    await nextTick();
    focusCurrentStepElement();
});

onMounted(() => {
    hydrateFromStorage();
    if (!hydratedFromStorage.value) applyPrefill(prefillData.value);

    if (bookingCompleted.value && isBrowser) {
        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', blockHistoryBack);
    }

    void nextTick().then(() => {
        focusCurrentStepElement();
    });
});

onBeforeUnmount(() => {
    if (isBrowser) {
        window.removeEventListener('popstate', blockHistoryBack);
    }
    if (priceAnimTimer) clearTimeout(priceAnimTimer);
});

watch(bookingCompleted, (completed) => {
    if (!isBrowser) return;

    if (completed) {
        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', blockHistoryBack);
    } else {
        window.removeEventListener('popstate', blockHistoryBack);
    }
});

// Submit
function handleStep1Next() {
    if (canProceedStep1.value) {
        goToStep(2);
    }
}

const attemptedNextStep3 = ref(false);
const canProceedStep3 = computed(() => {
    const hasName = client.value.name.trim().length > 0;
    const email = client.value.email.trim();
    const hasEmail = email.length > 0;
    const emailHasAt = email.includes('@');
    return hasName && hasEmail && emailHasAt;
});

function handleNext() {
    if (step.value === 2) {
        attemptedNextStep2.value = true;
        if (!canProceedStep2.value) {
            return;
        }
    }
    if (step.value === 3) {
        attemptedNextStep3.value = true;
        if (!canProceedStep3.value) {
            return;
        }
    }
    goToStep(step.value + 1);
}

async function getFreshCsrfToken() {
    const { data } = await axios.get('/csrf-token', {
        params: {
            t: Date.now(),
        },
        withCredentials: true,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
        },
    });

    return data?.csrfToken ?? null;
}

async function submitBooking() {
    isSubmitting.value = true;

    const payload = {
        // Type de trajet
        tripType: tripType.value,
        onewayDirection: tripType.value === 'oneway' ? onewayDirection.value : null,

        // Lieux
        from: from.value,
        dropTo: dropTo.value,
        hotel: hotel.value,

        // Passagers détaillés
        passengers: totalPassengers.value,
        adults: adults.value,
        children: children.value,
        babies: babies.value,

        // Équipement
        babySeat: babySeat.value,
        childSeat: childSeat.value,
        numberOfStrollers: numberOfStrollers.value,
        numberOfSuitcases: numberOfSuitcases.value,

        // Prix
        basePrice: basePrice.value,
        nightPrice: nightPrice.value,
        finalPrice: finalPrice.value,
        hasNightTrip: hasNightTrip.value,

        // Dates et heures pour aller simple
        dateOneWay: tripType.value === 'oneway' ? dateOneWay.value : null,
        timeOneWay: tripType.value === 'oneway' ? timeOneWayStr.value : null,
        pickUpOneWay: tripType.value === 'oneway' && onewayDirection.value === 'departure' ? pickUpTimeOneWay.value : null,

        // Dates et heures pour aller-retour
        dateArrival: tripType.value === 'roundtrip' ? dateArrival.value : null,
        timeArrival: tripType.value === 'roundtrip' ? timeArrivalStr.value : null,
        dateDeparture: tripType.value === 'roundtrip' ? dateDeparture.value : null,
        timeDeparture: tripType.value === 'roundtrip' ? timeDepartureStr.value : null,
        pickUpDeparture: tripType.value === 'roundtrip' ? pickUpTimeDeparture.value : null,

        // Informations de voyage
        travelType: travelType.value,
        travelNumber: travelNumber.value,
        vehicleType: vehicleType.value,

        // Informations client
        clientName: client.value.name,
        clientEmail: client.value.email,
        clientPhone: client.value.phone,
        clientNotes: client.value.notes,
        privacyConsent: privacyAccepted.value,

        // Locale pour les traductions dans l'email
        locale: locale.value,
    };

    const sendReservation = async (csrfToken: string | null) => {
        const requestPayload = csrfToken ? { ...payload, _token: csrfToken } : payload;

        return axios.post('/reservation', requestPayload, {
            withCredentials: true,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
            },
        });
    };

    try {
        let csrfToken = null;

        try {
            csrfToken = await getFreshCsrfToken();
        } catch (error) {
            console.warn('Unable to refresh CSRF token before reservation submit.', error);
        }

        let response;

        try {
            response = await sendReservation(csrfToken);
        } catch (error: any) {
            if (error?.response?.status !== 419) {
                throw error;
            }

            // Retry once with a freshly regenerated token to mitigate production race/caching issues.
            const retryToken = await getFreshCsrfToken();
            response = await sendReservation(retryToken);
        }

        if (isBrowser) {
            window.localStorage.removeItem(STORAGE_KEY);
        }

        bookingCompleted.value = true;
        reservationReference.value = response?.data?.reference || '';
        goToStep(6);
    } catch (error) {
        console.error('Reservation submit failed.', error);
    } finally {
        isSubmitting.value = false;
    }
}

function startNewBooking() {
    bookingCompleted.value = false;
    reservationReference.value = '';
    step.value = 1;

    tripType.value = 'oneway';
    onewayDirection.value = 'arrival';
    from.value = '';
    dropTo.value = '';
    hotel.value = '';

    adults.value = 1;
    children.value = 0;
    babies.value = 0;
    babySeat.value = 0;
    childSeat.value = 0;
    numberOfStrollers.value = 0;
    numberOfSuitcases.value = 0;

    dateOneWay.value = '';
    dateArrival.value = '';
    dateDeparture.value = '';
    timeOneWay.value = '';
    timeArrival.value = '';
    timeDeparture.value = '';

    travelType.value = '';
    travelNumber.value = '';
    vehicleType.value = '';
    client.value = { name: '', email: '', phone: '', notes: '' };
    privacyAccepted.value = false;

    attemptedNextStep3.value = false;
    attemptedNextStep2.value = false;

    if (isBrowser) {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(SHARED_TRIP_KEY);
    }
}
</script>

<style scoped>
@keyframes priceBump {
    0% {
        transform: scale(1);
    }
    35% {
        transform: scale(1.035);
    }
    100% {
        transform: scale(1);
    }
}

@keyframes priceFlashUp {
    0% {
        background-color: transparent;
        box-shadow: none;
    }
    30% {
        background-color: rgba(16, 185, 129, 0.14);
        box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.28);
    }
    100% {
        background-color: transparent;
        box-shadow: none;
    }
}

@keyframes priceFlashDown {
    0% {
        background-color: transparent;
        box-shadow: none;
    }
    30% {
        background-color: rgba(239, 68, 68, 0.14);
        box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.25);
    }
    100% {
        background-color: transparent;
        box-shadow: none;
    }
}

.price-bump {
    animation: priceBump 0.7s ease;
}

.price-up {
    border-radius: 0.75rem;
    animation:
        priceBump 0.7s ease,
        priceFlashUp 0.7s ease;
}

.price-down {
    border-radius: 0.75rem;
    animation:
        priceBump 0.7s ease,
        priceFlashDown 0.7s ease;
}
</style>
